
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import PriceTypeForm from "../global-components/price-type-form.vue";
import { ModalMessageEntities, OptionsClass } from "@/domain/entities/MainApp";
import { AddRenewRatesClientManagementApiRequestV2 } from "@/data/payload/api/ClientManagementApiRequest";
import { formatDateNormal } from "@/app/infrastructures/misc/Utils";
import { ClientManagementController } from "@/app/ui/controllers/ClientManagementController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";

class Props {
  modelValue = prop<any>({
    required: false,
    type: Object
  });
  clientName = prop<string>({
    type: String,
    default: ""
  });
  detailClient = prop<any>({
    type: Object,
    default: ""
  });
}

@Options({
  emits: ["update:modelValue"],
  components: {
    PriceTypeForm
  },
  watch: {
    "form.priceRateStartDate": "syncPeriodDateErrors",
    "form.priceRateEndDate": "syncPeriodDateErrors"
  }
})
export default class PopupRenewRate extends Vue.with(Props) {
  form = {
    isCustomRate: "retail",
    isInsurance: false,
    insurance: "",
    customRates: "",
    priceRateStartDate: "",
    priceRateEndDate: "",
    originCities: [] as OptionsClass[],
    discount: "" as any,
    versionName: "" as any,
    archiveFile: "" as any,
    retailBasedDistrict: false,
    versionBasic: "" as any,
    rateVersionId: "",
    useClientDiscountConfig: true
  }

  error = {
    isCustomRate: false,
    isInsurance: false,
    insurance: false,
    customRates: false,
    priceRateStartDate: false,
    priceRateEndDate: false,
    originCities: false,
    discount: false,
    versionName: false,
    archiveFile: false,
    retailBasedDistrict: false,
    versionBasic: false
  };

  get customPriceOption() {
    return [
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceRetail").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceRetailDescription").toString(),
        value: "retail"
      },
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceNormal").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceNormalDescription").toString(),
        value: "normal"
      },
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceSpecial").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceSpecialDescription").toString(),
        value: "khusus"
      },
      {
        disabled: false,
        label: this.$t("clientManagement.infoPriceClient.priceCombination").toString(),
        description: this.$t("clientManagement.infoPriceClient.priceCombinationDescription").toString(),
        value: "kombinasi"
      }
    ];
  }

  confirmation = false;

  onShowHidePopup() {
    if (this.modelValue) {
      this.$emit("update:modelValue", false);
      this.resetForm();
    } else {
      this.$emit("update:modelValue", true);
    }
  }

  getDefaultForm() {
    return {
      isCustomRate: "retail",
      isInsurance: false,
      insurance: "",
      customRates: "",
      priceRateStartDate: "",
      priceRateEndDate: "",
      originCities: [] as OptionsClass[],
      discount: "" as any,
      versionName: "" as any,
      archiveFile: "" as any,
      retailBasedDistrict: false,
      versionBasic: "" as any,
      rateVersionId: "",
      useClientDiscountConfig: true
    };
  }

  getDefaultError() {
    return {
      isCustomRate: false,
      isInsurance: false,
      insurance: false,
      customRates: false,
      priceRateStartDate: false,
      priceRateEndDate: false,
      originCities: false,
      discount: false,
      versionName: false,
      archiveFile: false,
      retailBasedDistrict: false,
      versionBasic: false
    };
  }

  resetForm() {
    this.form = this.getDefaultForm();
    this.error = this.getDefaultError();
    this.confirmation = false;
  }

  mounted() {
    this.syncPeriodDateErrors();
  }

  get insurancePercentage() {
    return this.form.isInsurance && this.form.insurance === "custom-rates"
      ? Number(Number("0." + this.form.customRates).toFixed(2))
      : 0;
  }

  get id() {
    return this.$route.params.id;
  }

  get priceType() {
    let priceTypeVal = "";
    const lowerCustomRate = this.form.isCustomRate.toLowerCase();
    if (lowerCustomRate === "retail") priceTypeVal = "retail";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value === "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "normal";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value !== "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "normal_custom_3lc";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value === "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "normal_retail_district";
    if (
      lowerCustomRate === "normal" &&
      this.form.originCities[0].value !== "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "normal_custom_3lc_retail_district";
    if (lowerCustomRate === "khusus" && !this.form.retailBasedDistrict)
      priceTypeVal = "custom_client";
    if (lowerCustomRate === "khusus" && this.form.retailBasedDistrict)
      priceTypeVal = "custom_client_retail_district";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value === "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "combination";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value !== "All" &&
      !this.form.retailBasedDistrict
    )
      priceTypeVal = "combination_custom_3lc";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value === "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "combination_retail_district";
    if (
      lowerCustomRate === "kombinasi" &&
      this.form.originCities[0].value !== "All" &&
      this.form.retailBasedDistrict
    )
      priceTypeVal = "combination_custom_3lc_retail_district";
    return priceTypeVal;
  }
  get priceRateRetailStartDate() {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);
    return startDate;
  }

  async addRenewRateV2() {
    MainAppController.setSnackbarPositivity(true);
    MainAppController.setSnackbar(false);
    let { discount } = this.form;
    if (!Array.isArray(discount)) discount = [];
    const findValueSemua = discount.find((item: any) => item.product.name === "SEMUA");
    const mapDiscountProduct =
    findValueSemua
    ? null
    : discount.filter((d: any) => d.product.name).map((item: any) => {
      return {
        product_type: item.product.name,
        discount: Number(item.discount.replace(/,/g, "."))
      }
    })
    const discountPrice = findValueSemua ? Number(discount[0].discount.replace(/,/g, ".")) : 0;

    const payload = new AddRenewRatesClientManagementApiRequestV2({
      clientId: Number(this.id),
      versionBasic:
        this.form.isCustomRate === "khusus"
          ? ""
          : this.form.isCustomRate === "retail"
          ? ""
          : this.form.versionBasic?.rateVersionName,
      isCustomRate: this.isMixRateBool,
      discount: discountPrice,
      discountProductTypes: mapDiscountProduct,
      startDate: this.$moment(
        this.form.isCustomRate === "retail"
          ? this.priceRateRetailStartDate
          : this.form.priceRateStartDate
      ).format("YYYY-MM-DDTHH:mm:ssZ"),
      endDate: this.$moment(
        this.form.isCustomRate === "retail"
          ? this.detailClient.clientBilling.endDate
          : this.form.priceRateEndDate
      ).format("YYYY-MM-DDTHH:mm:ssZ"),
      versionName:
        typeof this.form.versionName === "object"
          ? this.form.versionName.rateVersionName
          : this.form.versionName,
      archiveFile:
        typeof this.form.archiveFile === "object" ? this.form.archiveFile : "",
      isCustomRateOnly: this.isCustomRateBool,
      priceType: this.priceType,
      insuranceType: this.form.isInsurance
        ? this.form.insurance === "custom-rates" || this.form.insurance === "custom"
          ? "custom"
          : "bundling"
        : "",
      insurancePercentage: this.insurancePercentage,
      "3lcOrigin":
        this.form.originCities[0]?.value === "All"
          ? ""
          : this.form.originCities.map((oc: any) => oc.name).join(", "),
      useClientDiscountConfig: this.form.useClientDiscountConfig
    });
    const resp = await ClientManagementController.addRenewRatesV2(payload);
    if (resp) {
      ClientManagementController.getDetailV2(Number(this.id));
      this.onShowHidePopup();
      this.$router.push(`/client/client-management/${this.id}`).then(() => {
        MainAppController.setSnackbarMessage(
          this.$t("clientManagement.ratePopup.addPriceSuccess", { clientName: this.clientName }).toString()
        );
        MainAppController.setSnackbarTimeout(4000);
        MainAppController.setSnackbar(true);
      });
    }
  }

  get isBasicRateBool() {
    return this.form.isCustomRate === "normal";
  }
  get isMixRateBool() {
    return this.form.isCustomRate === "kombinasi";
  }
  get isCustomRateBool() {
    return this.form.isCustomRate === "khusus";
  }

  get handleRetail() {
    return (
      !this.form.isInsurance ||
      (this.form.isInsurance && this.form.insurance && !this.error.customRates)
    );
  }

  get handleNormal() {
    return (
      (this.form.versionBasic &&
        this.form.originCities.length &&
        this.useDiscountValidation &&
        !this.form.isInsurance) ||
      (this.form.versionBasic &&
        this.form.originCities.length &&
        this.useDiscountValidation &&
        this.form.isInsurance &&
        this.form.insurance &&
        !this.error.customRates)
    );
  }

  get handleKhusus() {
    return (
      (this.form.versionName &&
        this.form.archiveFile.name &&
        !this.form.isInsurance) ||
      (this.form.versionName &&
        this.form.archiveFile.name &&
        this.form.isInsurance &&
        this.form.insurance &&
        !this.error.customRates)
    );
  }

  get handleKombinasi() {
    return (
      (this.form.versionName &&
        this.form.versionBasic &&
        this.form.originCities.length &&
        this.useDiscountValidation &&
        this.form.archiveFile.name &&
        !this.form.isInsurance) ||
      (this.form.versionName &&
        this.form.versionBasic &&
        this.form.originCities.length &&
        this.useDiscountValidation &&
        this.form.archiveFile.name &&
        this.form.isInsurance &&
        this.form.insurance &&
        !this.error.customRates)
    );
  }

  get useDiscountValidation() {
    const discountValid = Array.isArray(this.form.discount) && this.form.discount.length > 0 && this.form.discount.every((item: any) => item?.product?.name && item?.discount);
    return this.form.useClientDiscountConfig
      ? discountValid && !this.error.discount
      : true;
  }

  get isValidRenewRate() {
    let result = false;
    if (this.form.isCustomRate === "retail") {
      if (this.handleRetail) result = true;
    }
    if (this.form.isCustomRate === "normal") {
      if (this.handleNormal) result = true;
    }
    if (this.form.isCustomRate === "khusus") {
      if (this.handleKhusus) result = true;
    }
    if (this.form.isCustomRate === "kombinasi") {
      if (this.handleKombinasi) result = true;
    }
    return (
      result && !this.error.priceRateStartDate && !this.error.priceRateEndDate
    );
  }

  get validateStartEndDate() {
    const startRaw = this.form.priceRateStartDate;
    const endRaw = this.form.priceRateEndDate;

    const startDateValue = startRaw ? new Date(startRaw as any) : null;
    const endDateValue = endRaw ? new Date(endRaw as any) : null;

    if (
      !startDateValue ||
      !endDateValue ||
      isNaN(startDateValue.getTime()) ||
      isNaN(endDateValue.getTime())
    ) {
      return false;
    }

    return startDateValue.getTime() >= endDateValue.getTime();
  }

  syncPeriodDateErrors() {
    const value = this.validateStartEndDate;
    this.error.priceRateStartDate = value;
    this.error.priceRateEndDate = value;
  }

  onConfirmRenewal() {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        title: this.$t("clientManagement.infoPriceClient.changeParentClientPriceQuestion").toString(),
        message: this.$t("clientManagement.infoPriceClient.changeParentClientPriceMessage").toString(),
        image: "badge-confirmation-general",
        textSuccess: this.$t("clientManagement.infoPriceClient.yesChange").toString(),
        textCancel: this.$t("clientManagement.infoPriceClient.back").toString(),
        onClose: () => {
          MainAppController.closeMessageModal();
        },
        onSubmit: () => {
          MainAppController.closeMessageModal();
          this.addRenewRateV2();
        }
      })
    );
  }
}
